// Sales/marketing "Get started" form shape + Google-Form entry-id mapping.
// Ported from the landing site (landing/components/marketing/fieldMapping.ts)
// so the register page can collect the same lead details and forward them to
// the sales team's Google Form. Keep the two copies in sync if either changes.

export interface ContactFormValues {
  name: string;
  email: string;
  contactNo: string;
  whoAreYou: 'Individual' | 'Organization' | '';
  individualJobRole: 'Active Student' | 'Developer' | 'Other' | '';
  schoolName?: string;
  stream?: string;
  yearsOfExperience?: '0-2 years' | '3-6 years' | '6+ years' | '';
  codingLanguages?: string;
  orgName?: string;
  orgSize?: '1-10' | '11-50' | '51-100' | '101-250' | '250+' | '';
  orgJobRole: 'Owner' | 'C-Level' | 'Board Member' | 'Director' | 'Manager' | 'Employee' | 'Other' | '';
  orgWebsite?: string;
}

export const GOOGLE_FORM_FIELD_MAPPING: Record<keyof ContactFormValues, string> = {
  name: 'entry.1525521551',
  email: 'entry.63196890',
  contactNo: 'entry.1853996449',
  whoAreYou: 'entry.1139822576',
  individualJobRole: 'entry.1605256762',
  schoolName: 'entry.428872932',
  stream: 'entry.1456931174',
  yearsOfExperience: 'entry.1556777276',
  codingLanguages: 'entry.422441240',
  orgName: 'entry.594207211',
  orgSize: 'entry.359105473',
  orgJobRole: 'entry.1761408129',
  orgWebsite: 'entry.620642980',
};

export const GOOGLE_FORM_ACTION_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSeLJy1UN-rMVfZXjflDF6US-iwTRae-iWrh6H5Yj65PDmd2EQ/formResponse';

/**
 * The Google Form is split into branching sections (pages), and Google DISCARDS
 * answers from any page not listed in `pageHistory` — without it only page 0
 * (name / email / contact / who-are-you) is ever recorded. The value must trace
 * the real branch path, and every REQUIRED field on a listed page must be sent,
 * or the whole submission is rejected (HTTP 400).
 *
 * Page map (from the live form's FB_PUBLIC_LOAD_DATA_):
 *   0 — Name, Email ID, Contact No., Who are you?
 *   1 — Organization Details   (Who are you = Organization)
 *   2 — Developer's Form       (Job Role = Developer)
 *   3 — Individual Details     (Who are you = Individual)
 *   4 — Student's Data Form    (Job Role = Active Student)
 */
export function pageHistoryFor(values: ContactFormValues): string {
  if (values.whoAreYou === 'Organization') return '0,1';
  if (values.whoAreYou === 'Individual') {
    if (values.individualJobRole === 'Developer') return '0,3,2';
    if (values.individualJobRole === 'Active Student') return '0,3,4';
    return '0,3'; // "Other" — no follow-up section
  }
  return '0';
}

/** Empty form value set — used to seed React state. */
export const EMPTY_CONTACT_FORM: ContactFormValues = {
  name: '',
  email: '',
  contactNo: '',
  whoAreYou: '',
  individualJobRole: '',
  schoolName: '',
  stream: '',
  yearsOfExperience: '',
  codingLanguages: '',
  orgName: '',
  orgSize: '',
  orgJobRole: '',
  orgWebsite: '',
};
